import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Truck, Plus, Trash2, Sparkles, ExternalLink } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Eyebrow, Spinner } from "@/components/tac";

const CATS = ["distributor", "manufacturer", "supplier", "subcontractor"];
const inp = "w-full rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none";

export default function Vendors() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["vendors"], queryFn: async () => (await api.get("/vendors")).data });
  const [f, setF] = useState({ name: "", category: "distributor", account_no: "", website: "" });
  const refresh = () => qc.invalidateQueries({ queryKey: ["vendors"] });

  const add = async () => {
    try { await api.post("/vendors", f); toast.success("Vendor added"); setF({ name: "", category: "distributor", account_no: "", website: "" }); refresh(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Remove this vendor?")) return; try { await api.delete(`/vendors/${id}`); refresh(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const seedAv = async () => { try { const { data: r } = await api.post("/vendors/seed-av"); toast.success(`Added ${r.added} AV vendor(s)`); refresh(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  if (isLoading) return <div className="p-10"><Spinner /></div>;

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="flex items-start justify-between">
        <div><Eyebrow>Supply chain</Eyebrow><h1 className="mt-2 font-display text-3xl font-bold text-white">Vendors</h1>
          <p className="mt-2 text-sm text-slate-400">Distributors, manufacturers and suppliers. Vendors tag material costs and receipt auto-imports.</p></div>
        <button data-testid="seed-av-vendors-btn" onClick={seedAv} className="mt-2 inline-flex items-center gap-2 rounded-full border border-cyan-500/40 bg-cyan-950/30 px-4 py-2.5 text-sm text-cyan-300 hover:bg-cyan-900/40"><Sparkles className="h-4 w-4" /> Add AV distributors</button>
      </div>

      <div className="mt-6 grid gap-2 rounded-xl border border-slate-800 p-3 sm:grid-cols-[1fr_150px_140px_1fr_auto]" data-testid="vendor-add-form">
        <input data-testid="vendor-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Vendor name" className={inp} />
        <select data-testid="vendor-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={inp}>{CATS.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        <input data-testid="vendor-account" value={f.account_no} onChange={(e) => setF({ ...f, account_no: e.target.value })} placeholder="Account #" className={inp} />
        <input data-testid="vendor-website" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} placeholder="Website" className={inp} />
        <button data-testid="vendor-add-btn" onClick={add} disabled={!f.name.trim()} className="btn-amber inline-flex items-center justify-center gap-1 rounded-lg px-3 py-2.5 text-xs disabled:opacity-50"><Plus className="h-3.5 w-3.5" /> Add</button>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {data.map((v) => (
          <div key={v.id} className="card-tac flex items-start justify-between p-4" data-testid={`vendor-${v.id}`}>
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-amber-500/10 text-amber-400"><Truck className="h-5 w-5" /></div>
              <div>
                <div className="font-display text-sm font-semibold text-white">{v.name}</div>
                <div className="font-mono-tac text-[10px] uppercase tracking-widest text-slate-500">{v.category}{v.account_no ? ` · acct ${v.account_no}` : ""}</div>
                {v.website && <a href={v.website.startsWith("http") ? v.website : `https://${v.website}`} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-cyan-400 hover:underline">{v.website} <ExternalLink className="h-3 w-3" /></a>}
              </div>
            </div>
            <button data-testid={`vendor-delete-${v.id}`} onClick={() => del(v.id)} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {!data.length && <div className="card-tac py-12 text-center text-slate-500 sm:col-span-2" data-testid="vendors-empty">No vendors yet. Add your distributors or tap "Add AV distributors".</div>}
      </div>
    </div>
  );
}
